import type {QueryHookOptions} from '@apollo/client';
import {useDispatch} from 'react-redux';

import {useStatQuery} from 'web-app/react/staffing-attendance/hooks/queries/use-stat-query';
import {type ImmerWrappedQueryResult} from 'web-app/react/staffing-attendance/hooks/use-immer-query-wrapper';

import {FetchPricingGroups} from './fetch-pricing-groups.query';
import {
    type FetchPricingGroupsQuery,
    type FetchPricingGroupsQueryVariables,
} from './__generated__/fetch-pricing-groups.query.api-types';
import {useSubscribableUpdatePricingGroupsMutationResult} from '../settings/pricing-groups/use-update-pricing-groups-mutation';
import {populateRedux} from './redux-side-effects/pricing-groups/pricing-groups-actions';

export function useFetchPricingGroupsQuery(
    options: QueryHookOptions<FetchPricingGroupsQuery, FetchPricingGroupsQueryVariables>,
): ImmerWrappedQueryResult<FetchPricingGroupsQuery, FetchPricingGroupsQueryVariables> {
    const dispatch = useDispatch();
    const queryResult = useStatQuery(FetchPricingGroups, {
        ...(options ?? {}),
        onCompleted: data => {
            options?.onCompleted?.(data);
            // the timeout is needed since this led to an endless loop in react 18
            setTimeout(() => dispatch({type: populateRedux.type, payload: {data}}), 200);
        },
    });

    useSubscribableUpdatePricingGroupsMutationResult(result => {
        if (!result) {
            return;
        }

        queryResult.updateQuery(cache => {
            let newList: typeof cache.finance.pricingGroups.list = result.finance.pricingGroups.setPricingGroups;

            if (options.variables?.includeDeleted) {
                newList = newList.concat(
                    cache.finance.pricingGroups.list
                        // deleted groups are not returned in the mutation result.
                        // in case we have deleted groups in the cache (only if given variable is set)
                        // we need to make sure they stay in the cache
                        .filter(group => group.deleted),
                );

                newList = newList.concat(
                    cache.finance.pricingGroups.list
                        // keep those elemetns in teh cache, that got deleted.
                        // only keep those however, that have a pricing group id
                        .filter(group => !group.deleted && group.pricingGroupId)
                        // add those elements, that were deleted to
                        .filter(
                            group =>
                                !result.finance.pricingGroups.setPricingGroups.find(
                                    pricingGroup => group.pricingGroupId === pricingGroup.pricingGroupId,
                                ),
                        )
                        .map(group => ({...group, deleted: true})),
                );
            }

            cache.finance.pricingGroups.list = newList;

            // the timeout is needed since this led to an endless loop in react 18
            setTimeout(() => dispatch({type: populateRedux.type, payload: {data: cache}}), 200);
            return cache;
        });
    });

    return queryResult;
}
